/**
 * Trainer Rubric Detail
 *
 * View, edit, publish, and deprecate rubrics.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiClient } from '../../lib/api';

import { RequestPanel, StatusBadge } from '../../components/RequestPanel';
import { Code } from '../../components/JsonViewer';
import type { ApiResult, Rubric } from '../../lib/types';

export function TrainerRubricDetail() {
  const { id } = useParams<{ id: string }>();
  const [rubric, setRubric] = useState<Rubric | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [result, setResult] = useState<ApiResult<Rubric> | null>(null);
  const [showConfirm, setShowConfirm] = useState<'publish' | 'deprecate' | null>(null);

  const loadRubric = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const res = await apiClient.getRubric(id);
    setResult(res);
    if (res.response.data) {
      setRubric(res.response.data);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadRubric();
  }, [loadRubric]);

  const handlePublish = async () => {
    if (!id) return;
    setActionLoading(true);
    const res = await apiClient.publishRubric(id);
    setResult(res);
    setActionLoading(false);
    setShowConfirm(null);
    // Re-fetch server state per PRD requirement
    if (res.response.status === 200) {
      await loadRubric();
    }
  };

  const handleDeprecate = async () => {
    if (!id) return;
    setActionLoading(true);
    const res = await apiClient.deprecateRubric(id);
    setResult(res);
    setActionLoading(false);
    setShowConfirm(null);
    // Re-fetch server state per PRD requirement
    if (res.response.status === 200) {
      await loadRubric();
    }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <Link to="/console/trainer/rubrics" style={{ color: 'var(--text-tertiary)' }}>
          &larr; Back to Rubrics
        </Link>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h1>Rubric Detail</h1>
        {rubric && (
          <div style={{ display: 'flex', gap: 8 }}>
            {rubric.status === 'draft' && (
              <>
                <Link to={`/console/trainer/rubrics/${id}/edit`} className="btn btn-secondary">
                  Edit
                </Link>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowConfirm('publish')}
                  disabled={actionLoading}
                >
                  Publish
                </button>
              </>
            )}
            {rubric.status === 'published' && (
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirm('deprecate')}
                disabled={actionLoading}
                style={{ color: 'var(--semantic-error)' }}
              >
                Deprecate
              </button>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            background: showConfirm === 'publish' ? 'var(--semantic-success-light)' : 'var(--semantic-error-light)',
            borderColor: showConfirm === 'publish' ? 'var(--semantic-success-border)' : 'var(--semantic-error-border)',
          }}
        >
          <h3 style={{ marginBottom: 8 }}>
            {showConfirm === 'publish' ? 'Publish Rubric?' : 'Deprecate Rubric?'}
          </h3>
          <p className="text-muted" style={{ marginBottom: 16 }}>
            {showConfirm === 'publish'
              ? 'Published rubrics can be used for assessments. This action cannot be undone.'
              : 'Deprecated rubrics cannot be used for new assessments. Existing assessments are unaffected.'}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className={`btn ${showConfirm === 'publish' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={showConfirm === 'publish' ? handlePublish : handleDeprecate}
              disabled={actionLoading}
              style={showConfirm === 'deprecate' ? { color: 'var(--semantic-error)' } : undefined}
            >
              {actionLoading
                ? 'Processing...'
                : showConfirm === 'publish'
                ? 'Yes, Publish'
                : 'Yes, Deprecate'}
            </button>
            <button className="btn btn-secondary" onClick={() => setShowConfirm(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {rubric && (
        <>
          {/* Summary */}
          <div className="card">
            <h2>Summary</h2>
            <table className="table" style={{ maxWidth: 600 }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 500, width: 180 }}>ID</td>
                  <td>
                    <Code>{rubric.id}</Code>
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Name</td>
                  <td style={{ fontWeight: 500 }}>{rubric.name}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Version</td>
                  <td>{rubric.version}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Status</td>
                  <td>
                    <StatusBadge status={rubric.status} />
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Passing Score</td>
                  <td>{rubric.passingScore}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Max Score</td>
                  <td>{rubric.maxScore}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Dimensions</td>
                  <td>{rubric.dimensions.length}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Created At</td>
                  <td>{new Date(rubric.createdAt).toLocaleString()}</td>
                </tr>
                {rubric.publishedAt && (
                  <tr>
                    <td style={{ fontWeight: 500 }}>Published At</td>
                    <td>{new Date(rubric.publishedAt).toLocaleString()}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {rubric.description && (
              <div style={{ marginTop: 16 }}>
                <h3>Description</h3>
                <p className="text-muted">{rubric.description}</p>
              </div>
            )}
          </div>

          {/* Dimensions */}
          <div className="card">
            <h2>Dimensions</h2>
            {rubric.dimensions.map((dim, i) => (
              <div
                key={dim.id}
                style={{
                  marginBottom: 16,
                  paddingBottom: 16,
                  borderBottom: i < rubric.dimensions.length - 1 ? '1px solid var(--border-default)' : 'none',
                }}
              >
                <h3>
                  {dim.name}{' '}
                  <span className="text-muted text-small">(weight: {dim.weight})</span>
                </h3>
                {dim.description && (
                  <p className="text-muted text-small" style={{ marginBottom: 8 }}>
                    {dim.description}
                  </p>
                )}
                <table className="table" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Rule ID</th>
                      <th>Type</th>
                      <th>Pattern</th>
                      <th>Score</th>
                      <th>Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dim.rules.map((rule) => (
                      <tr key={rule.id}>
                        <td>
                          <Code>{rule.id}</Code>
                        </td>
                        <td>{rule.type}</td>
                        <td
                          className="mono"
                          style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {rule.pattern}
                        </td>
                        <td>{rule.score}</td>
                        <td>{rule.required ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Actions */}
          {rubric.status === 'published' && (
            <div className="card">
              <h2>Actions</h2>
              <Link to={`/console/trainer/assessments/new?rubricId=${rubric.id}`} className="btn btn-primary">
                Create Assessment with this Rubric
              </Link>
            </div>
          )}
        </>
      )}

      <RequestPanel result={result} loading={loading} />
    </div>
  );
}
